import { clone } from 'radash';
import {
  approxGT,
  approxGTE,
  approxLT,
  approxLTE,
  isApproxWindowSubset
} from '@goldcast/player-config/src/utils/compareUtils';
import {
  ClipDeletes,
  ClipEdits
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import {
  FormattedDefaultTranscript,
  FormattedTranscriptItem,
  FormattedTranslatedCaptions,
  WordType
} from '@goldcast/player-config/src/types/transcript-types';
import { EditedWord } from '@goldcast/player-config/src/types/clip-types';

class Word {
  type: WordType;
  content: string;
  start_time: number;
  end_time: number;
  processed_start_time: number;
  processed_end_time: number;
  speaker_label: string;
  srt_index: number;
  hidden: boolean;
  index: number;
  is_filler: boolean;

  constructor(
    type: WordType,
    content: string,
    start_time: number | string,
    end_time: number | string,
    speaker_label: string,
    srt_index: number,
    index: number = 0,
    hidden: boolean = false,
    is_filler: boolean = false
  ) {
    this.type = type;
    this.content = content;
    this.start_time = parseFloat(start_time.toString());
    this.end_time = parseFloat(end_time.toString());
    this.processed_start_time = this.start_time;
    this.processed_end_time = this.end_time;
    this.speaker_label = speaker_label;
    this.srt_index = srt_index;
    this.hidden = hidden;
    this.index = index;
    this.is_filler = is_filler;
  }

  toDict() {
    return {
      type: this.type,
      content: this.content,
      start_time: this.start_time,
      end_time: this.end_time,
      processed_start_time: this.processed_start_time,
      processed_end_time: this.processed_end_time,
      speaker_label: this.speaker_label,
      srt_index: this.srt_index,
      hidden: this.hidden,
      index: this.index,
      is_filler: this.is_filler
    };
  }
}

const MIN_WORDS_PER_SRT_INDEX = 3;

type EditActions = ClipEdits;
type DeleteActions = ClipDeletes;
type MergeAction = EditedWord;

export class TranscriptProcessor {
  private start_time: number;
  private end_time: number;
  private _transcript: FormattedDefaultTranscript = [];
  private _original_portion: Record<string, Word> | null = null;
  private _cached_portion: Record<string, Word> | null = null;
  private _excludes: [number, number][] | null = null;
  private _translated_captions?: FormattedTranslatedCaptions;

  constructor(
    start_time: number,
    end_time: number,
    transcript: FormattedDefaultTranscript,
    translated_captions?: FormattedTranslatedCaptions
  ) {
    this.start_time = start_time;
    this.end_time = end_time;
    this._transcript = transcript;
    this._translated_captions = translated_captions;
  }

  private _isWithinTimeRange(item: FormattedTranscriptItem): boolean {
    const start = item.start_time;
    const end = item.end_time;
    return (
      approxGTE(start, this.start_time) &&
      approxLTE(start, this.end_time) &&
      approxGTE(end, this.start_time) &&
      approxLTE(end, this.end_time)
    );
  }

  private _processTranslatedCaptions(): Record<string, Word> {
    const portionList: Word[] = [];

    for (const item of this._translated_captions ?? []) {
      if (item.type === 'pronunciation' && this._isWithinTimeRange(item)) {
        portionList.push(
          new Word(
            item.type,
            item.content,
            item.start_time,
            item.end_time,
            item.speaker_label,
            item.srt_index || 0,
            0,
            false,
            item.is_filler || false
          )
        );
      } else if (portionList.length && item.type !== 'pronunciation') {
        portionList[portionList.length - 1].content += item.content;
      } else if (approxGT(item.start_time, this.end_time)) {
        break;
      }
    }

    return Object.fromEntries(portionList.map(w => [w.start_time.toString(), w]));
  }

  public getPortionBetweenTimes(): Record<string, Word> {
    if (this._cached_portion) {
      return this._cached_portion;
    }

    const portionList: Word[] = [];

    if (this._translated_captions) {
      this._cached_portion = this._processTranslatedCaptions();
    } else {
      for (const item of this._transcript) {
        if (item.type === 'pronunciation' && this._isWithinTimeRange(item)) {
          portionList.push(
            new Word(
              item.type,
              item.content,
              item.start_time,
              item.end_time,
              item.speaker_label,
              item.srt_index || 0,
              0,
              false,
              item.is_filler || false
            )
          );
        } else if (portionList.length && item.type !== 'pronunciation') {
          portionList[portionList.length - 1].content += item.content;
        } else if (approxGT(item.start_time, this.end_time)) {
          break;
        }
      }

      this._cached_portion = Object.fromEntries(portionList.map(w => [w.start_time.toString(), w]));
    }

    this._original_portion = JSON.parse(JSON.stringify(this._cached_portion));
    return this._cached_portion!;
  }

  public applyDeleteActions(deleteActions: DeleteActions) {
    for (const { bounds } of Object.values(deleteActions)) {
      for (const [key, item] of Object.entries(this._cached_portion || {}).sort(
        ([keyA], [keyB]) => parseFloat(keyA) - parseFloat(keyB)
      )) {
        if (isApproxWindowSubset([item.start_time, item.end_time], bounds)) {
          delete this._cached_portion![key];
        }
      }
    }
  }

  public applyEditActions(editActions: EditActions) {
    for (const [_, edit] of Object.entries(editActions).sort(
      ([timeA], [timeB]) => parseFloat(timeA) - parseFloat(timeB)
    )) {
      // key of edit dictionary can have decimals e.g. 12.0 which is not equal to 12
      const item = this._cached_portion?.[edit?.start_time];
      if (item) {
        if (edit.word === '') {
          item.hidden = true;
        } else {
          item.content = edit.word;
        }
      }
    }
  }

  public applyMergeActions(mergeActions: MergeAction[]) {
    const sortedArray = clone(mergeActions);
    for (const action of sortedArray.sort((a, b) => a.start_time - b.start_time)) {
      const start = action.start_time;
      const end = action.end_time;
      const keys = Object.keys(this._cached_portion || {})
        .filter(t => approxGTE(parseFloat(t), start) && approxLT(parseFloat(t), end))
        .sort((a, b) => parseFloat(a) - parseFloat(b));

      if (keys.length) {
        const first = { ...this._cached_portion![keys[0]] };
        first.content = action.word;
        first.start_time = start;
        first.end_time = end;

        for (const key of keys.slice(1)) {
          delete this._cached_portion![key];
        }
      }
    }
  }

  public static getExcludesAdjustedTime(time: number, excludes: [number, number][] | null) {
    const adjustedTime =
      excludes
        ?.filter(e => approxLT(e[0], time))
        .map(e => Math.min(e[1], time) - e[0])
        .reduce((a, b) => a + b, 0) ?? time;
    return adjustedTime;
  }

  public applyShift(deleteActions: DeleteActions) {
    if (!this._cached_portion) {
      return;
    }

    this.getExcludes(deleteActions);

    const words = Object.values(this._cached_portion).sort((a, b) => a.start_time - b.start_time);

    const excludes = this._excludes;
    // handle all 3 scenarios, prefix partial delete, suffix partial delete, middle partial delete
    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      const durationStart = TranscriptProcessor.getExcludesAdjustedTime(word.start_time, excludes);
      const durationEnd = TranscriptProcessor.getExcludesAdjustedTime(word.end_time, excludes);

      word.processed_start_time -= durationStart + this.start_time;
      word.processed_end_time -= durationEnd + this.start_time;
      word.index = i;
    }
  }

  public balanceCaptions() {
    if (!this._cached_portion) {
      return;
    }

    const srtGroups: Record<number, string[]> = {};
    for (const [key, word] of Object.entries(this._cached_portion).sort(
      ([keyA], [keyB]) => parseFloat(keyA) - parseFloat(keyB)
    )) {
      if (!word.hidden) {
        if (!srtGroups[word.srt_index]) {
          srtGroups[word.srt_index] = [];
        }
        srtGroups[word.srt_index].push(key);
      }
    }

    const sortedIndexes = Object.keys(srtGroups)
      .map(Number)
      .sort((a, b) => a - b);

    for (let i = 1; i < sortedIndexes.length; i++) {
      const prevIdx = sortedIndexes[i - 1];
      const currIdx = sortedIndexes[i];

      if (srtGroups[prevIdx].length < MIN_WORDS_PER_SRT_INDEX) {
        const needed = MIN_WORDS_PER_SRT_INDEX - srtGroups[prevIdx].length;
        const wordsToMove = srtGroups[currIdx].splice(0, needed);

        for (const key of wordsToMove) {
          this._cached_portion![key].srt_index = prevIdx;
          srtGroups[prevIdx].push(key);
        }
      }
    }

    return this._cached_portion;
  }

  public getFinalCaptions(editActions: EditActions, deleteActions: DeleteActions, mergeActions: MergeAction[]) {
    this.getPortionBetweenTimes();
    this.applyDeleteActions(deleteActions);
    this.applyMergeActions(mergeActions);
    this.applyEditActions(editActions);
    this.applyShift(deleteActions);
    this.balanceCaptions();
    return Object.entries(this._cached_portion!)
      .sort(([keyA], [keyB]) => parseFloat(keyA) - parseFloat(keyB))
      .map(([_, item]) => item.toDict());
  }

  public getExcludes(deleteActions: DeleteActions): [number, number][] {
    if (this._excludes) {
      return this._excludes;
    }

    const sortedDeleteActions = Object.values(deleteActions).sort((a, b) => a.bounds[0] - b.bounds[0]);

    const excludePairs: [number, number][] = [];

    for (const deleteAction of sortedDeleteActions) {
      const start = deleteAction.bounds[0];
      const end = deleteAction.bounds[1];
      if (approxGT(end, this.start_time) && approxLT(start, this.end_time)) {
        excludePairs.push([Math.max(start, this.start_time), Math.min(end, this.end_time)]);
      }
    }

    this._excludes = excludePairs;
    return this._excludes;
  }

  public getDuration(deleteActions: DeleteActions) {
    const excludes = this.getExcludes(deleteActions);
    let duration = this.end_time - this.start_time;
    for (const exclude of excludes) {
      duration -= exclude[1] - exclude[0];
    }
    return duration;
  }
}
